import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getListViewState, saveListViewState, clearListViewState, isProfilePath } from '../utils/listViewState';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import {
    FiSearch,
    FiMoreVertical,
    FiX,
} from 'react-icons/fi';
import { Spin } from 'antd';
import useBlockScrollThrough from '../hooks/useBlockScrollThrough';
import { LoadingOutlined } from '@ant-design/icons';
import PageDataLoader from 'components/pageDataLoader';
import useFillToViewport from '../hooks/useFillToViewport';
import { COLORS } from '../utils/constants';
import { STUDENT_ROWS, TOTAL_RECORDS } from '../utils/staffTableData';
import { getStaffListV3, searchStaffV3, updateStaffStatusV3, bulkUpdateStaffStatus, getStaffStats } from 'actions/userManagementV3';
import { setUserDetails, updateUserToTranferData } from 'actions/userManagementV2';
import { Api, Notification } from 'services';
import FilterIcon from '../../../pages/userManagementV2/dashboardV3/filter-line.svg';
import SortIcon from '../../../pages/userManagementV2/dashboardV3/swap-half-vertical-reverse.svg';
import DownloadIcon from '../../../pages/userManagementV2/dashboardV3/download.svg';
import TeacherIcon from '../../../Assets/userManagementV3/teacherIcon.svg';
import OperationIcon from '../../../Assets/userManagementV3/operationIcon.svg';
import GridIcon from '../../../Assets/userManagementV3/gridIcon.svg';
import ListIcon from '../../../Assets/userManagementV3/listIcon.svg';
// Same prev/next arrows DateRangeFilterPopover.jsx (the calendar) uses.
import ArrowLeft from '../../../pages/userManagementV2/dashboardV3/arrow-single-left.svg';
import ArrowRight from '../../../pages/userManagementV2/dashboardV3/arrow-single-right.svg';
import illNoData from '../../../Assets/ill-no-data.svg';
import moment from 'moment';
import { fluid } from '../utils/responsive';
import { exportToExcel } from '../utils/exportToExcel';
import UserActionConfirmModal from '../../../pages/userManagementV3/localComponent/userActionConfirmModal';
import DownloadFileModal from './DownloadFileModal';
import PageSizeRange from './PageSizeRange';
import { scrollToTableTop } from '../utils/scrollToTableTop';

const STAFF_EXPORT_FIELDS = [
    { id: 'name', label: 'Name', getValue: (row) => row.name || '—' },
    { id: 'phone', label: 'Phone Number', getValue: (row) => formatPhoneNumber(row.phone) || '—' },
    { id: 'email', label: 'Email ID', getValue: (row) => row.email || '—' },
    { id: 'role', label: 'Role', getValue: (row) => row.role ? (row.role.toLowerCase().includes('operation') ? 'Operations' : row.role) : '—' },
    { id: 'access', label: 'Access', getValue: (row) => row.access || '—' },
    { id: 'status', label: 'Status', getValue: (row) => row.status || '—' },
    { id: 'joinedDate', label: 'Joined Date', getValue: (row) => formatJoinedDate(row.joinedOn) },
];

const COLUMN_TEMPLATE = `${fluid(36)} minmax(${fluid(220)},1.5fr) minmax(${fluid(180)},1.4fr) minmax(${fluid(120)},0.9fr) minmax(${fluid(140)},1fr) ${fluid(100)} ${fluid(76)}`;

const ROLE_PILLS = [
    { value: "All", label: "All" },
    { value: "Teacher", label: "Teachers" },
    { value: "Operation", label: "Operations" },
];
const STATUS_PILLS = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
];
const SORT_PILLS = [
    { value: "alphabetic", label: "Alphabetic" },
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
];

// UI status ("Active"/"Inactive") <-> the real backend enum, which is only
// "Approved" / "Suspended" / "Deleted" (there is no "Active" status on the
// API side — "Approved" is what an activated/active staff record holds).
const toApiStatus = (uiStatus) => (uiStatus === "Active" ? "Approved" : "Suspended");

const resolveAvatarUrl = (image) => {
    if (!image || typeof image !== 'string') return '';
    const trimmed = image.trim();
    if (!trimmed || trimmed.toLowerCase() === 'default.png' || trimmed.toLowerCase().includes('default.png') || trimmed.toLowerCase() === 'default.jpg') {
        return '';
    }
    return trimmed;
};

const AVATAR_COLORS = ["#594099", "#1982C4", "#8AC926", "#F5A623", "#FF6B6B", "#4ECDC4", "#D97706", "#059669"];

const getInitials = (name = "") => {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (first + last).toUpperCase();
};

const formatJoinedDate = (value) => {
    if (!value) return "—";
    const m = moment(value);
    if (!m.isValid()) return value;
    return m.format("DD MMM YYYY");
};

const formatPhoneNumber = (phone) => {
    if (!phone || typeof phone !== 'string') return phone || '';
    const trimmed = phone.trim();
    if (trimmed.startsWith('+91') && trimmed.length > 3 && !trimmed.startsWith('+91 ')) {
        return `+91 ${trimmed.slice(3).trim()}`;
    }
    return trimmed;
};

// Maps a `/userv2/staff` API record onto the row shape this table already
// renders (previously fed by the static STAFF_ROWS mock). API status values
// ("Active" / "Suspended") are normalised to the UI's existing "Active" /
// "Inactive" vocabulary so none of the render/filter logic below has to change.
// role is left as whatever the API actually sent (no "Teacher" fallback) —
// defaulting an unrecognized/missing role to "Teacher" used to silently
// disguise non-staff records (e.g. a student account with no roles field)
// as a real teacher row instead of surfacing the mismatch; isKnownStaffRole
// below uses this raw value to filter those out before they ever render.
// The full-list cache (allStaffCacheRef) holds both active and inactive staff
// so any status filter / clearing a search can show rows instantly while the
// API call for that view is still in flight. A fetch of one status replaces
// only that status's part of the cache.
const staffIdOf = (item) => item?.id ?? item?.userId;
const mergeStaffCache = (cacheRef, list, uiStatus) => {
    if (uiStatus === "All") {
        cacheRef.current = list;
        return;
    }
    const ids = new Set(list.map(staffIdOf));
    const keep = cacheRef.current.filter((item) => !ids.has(staffIdOf(item)) && mapApiRecordToRow(item).status !== uiStatus);
    cacheRef.current = [...list, ...keep];
};

const mapApiRecordToRow = (record) => {
    const rawImage = record?.avatar || record?.image || record?.basicprofile?.image || record?.basicprofile?.avatar || '';
    const rawRole = (Array.isArray(record?.roles) && record.roles.length > 0)
        ? record.roles[0]
        : ((Array.isArray(record?.role) && record.role.length > 0)
            ? record.role[0]
            : (record?.role || record?.userRole || ""));
    const roleString = typeof rawRole === "string" ? rawRole : (rawRole ? String(rawRole) : "");
    return {
        id: record?.id,
        name: record?.name || "N/A",
        image: resolveAvatarUrl(rawImage),
        email: record?.email || "",
        phone: formatPhoneNumber(record?.phone || record?.mobileNo || "N/A"),
        role: roleString,
        access: record?.access || record?.privilage?.split?.(",")?.[0] || "No access",
        joinedOn: record?.createdAt || "",
        status: record?.status === "Suspended" ? "Inactive" : "Active",
    };
};

// Only roles this table's own Role filter (ROLE_PILLS below) knows about are
// real staff — anything else (missing role, "Student", ...) means
// GET /userv2/staff hasn't actually scoped to staff-only records, which is a
// backend response issue, not something to paper over here. Dropped instead
// of shown mislabeled, with a console warning so a stray record is visible
// during backend debugging instead of silently vanishing.
const isKnownStaffRole = (role) => ["teacher", "operation", "operations"].includes(String(role || "").trim().toLowerCase());

const Avatar = React.memo(({ name, image }) => {
    const [imgError, setImgError] = useState(false);
    const resolvedImage = resolveAvatarUrl(image);
    const color = AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length] || AVATAR_COLORS[0];

    useEffect(() => {
        setImgError(false);
    }, [resolvedImage]);

    if (resolvedImage && !imgError) {
        return (
            <img
                src={resolvedImage}
                alt={name}
                onError={() => setImgError(true)}
                style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                }}
            />
        );
    }
    return (
        <div style={{
            width: "44px",
            height: "44px",
            borderRadius: "50%",
            backgroundColor: `${color}1A`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "14px",
            fontWeight: 700,
            flexShrink: 0,
        }}>
            {getInitials(name)}
        </div>
    );
});
Avatar.displayName = 'Avatar';

// Grid-card-only avatar: 60px initials circle, per spec — bigger than the
// list-view Avatar above, so it gets its own component rather than resizing
// the shared one (which would blow out the 72px-tall list rows). Keeps the
// existing per-name hash color (a spec-instance color like #0AB1E6-on-
// #D8F6FF is really just that tint pattern — ${color}1A already produces
// the same light-tinted-background-with-solid-text look for whichever
// color a given name hashes to, so every staff member keeps a
// distinguishable avatar color instead of everyone rendering the same blue).
const GridAvatar = React.memo(({ name, image }) => {
    const [imgError, setImgError] = useState(false);
    const resolvedImage = resolveAvatarUrl(image);
    const color = AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length] || AVATAR_COLORS[0];

    useEffect(() => {
        setImgError(false);
    }, [resolvedImage]);

    if (resolvedImage && !imgError) {
        return (
            <img
                src={resolvedImage}
                alt={name}
                onError={() => setImgError(true)}
                style={{
                    width: fluid(60),
                    height: fluid(60),
                    borderRadius: "50%",
                    objectFit: "cover",
                    flexShrink: 0,
                }}
            />
        );
    }
    return (
        <div style={{
            width: fluid(60),
            height: fluid(60),
            borderRadius: "50%",
            backgroundColor: `${color}1A`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "Roboto, sans-serif",
            fontSize: fluid(24),
            fontWeight: 400,
            flexShrink: 0,
        }}>
            {getInitials(name)}
        </div>
    );
});
GridAvatar.displayName = 'GridAvatar';

// Status dot no longer shown on grid cards.
/*
const StatusDot = React.memo(({ status }) => (
    <span style={{
        width: fluid(8),
        height: fluid(8),
        borderRadius: "50%",
        backgroundColor: status === "Active" ? "#00A566" : "#AEAEAE",
        flexShrink: 0,
        display: "inline-block",
    }} />
));
StatusDot.displayName = 'StatusDot';
*/

const StatusBadge = React.memo(({ status }) => {
    const isActive = status === "Active";
    return (
        <span style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: "22px",
            width: isActive ? "54px" : "63px",
            padding: "0 10px",
            border: isActive ? "1px solid #34D399" : "1px solid #FBBF24",
            borderRadius: "4px",
            fontFamily: "Roboto, sans-serif",
            fontSize: "12px",
            fontWeight: isActive ? 600 : 500,
            letterSpacing: 0,
            backgroundColor: isActive ? "#ECFDF5" : "#FFFBEB",
            color: isActive ? "#059669" : "#D97706",
            whiteSpace: "nowrap",
            boxSizing: "border-box",
        }}>
            {status}
        </span>
    );
});
StatusBadge.displayName = 'StatusBadge';

const RoleBadge = React.memo(({ role = "" }) => {
    const roleText = typeof role === "string" ? role : (Array.isArray(role) ? role[0] : (role ? String(role) : ""));
    const isOperations = String(roleText || "").toLowerCase().includes("operation");
    const displayRole = isOperations ? "Operations" : roleText;
    return (
        <div style={{ display: "flex", alignItems: "center", gap: fluid(10), minWidth: 0 }}>
            <img
                src={isOperations ? OperationIcon : TeacherIcon}
                alt={displayRole}
                style={{ width: fluid(40), height: fluid(40), flexShrink: 0 }}
            />
            <span style={{ fontSize: fluid(19), fontWeight: 400, fontFamily: "Roboto, sans-serif", color: COLORS.textBlack, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {displayRole}
            </span>
        </div>
    );
});
RoleBadge.displayName = 'RoleBadge';

const Checkbox = React.memo(({ checked, indeterminate = false, onChange, ariaLabel, disabled = false }) => {
    const filled = !disabled && (checked || indeterminate);
    return (
        <span
            role="checkbox"
            aria-checked={indeterminate ? "mixed" : checked}
            aria-disabled={disabled}
            aria-label={ariaLabel}
            onClick={(e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                if (disabled) return;
                onChange && onChange(e);
            }}
            style={{
                width: fluid(24),
                height: fluid(24),
                borderRadius: fluid(6),
                border: `1.5px solid ${disabled ? "#D0D0D0" : (filled ? COLORS.primary : "#B3B3B3")}`,
                backgroundColor: disabled ? "#EFEFEF" : (filled ? COLORS.primary : "#FFFFFF"),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
                flexShrink: 0,
            }}
        >
            {!disabled && (
                indeterminate ? (
                    <svg width="12" height="10" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4H9" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                ) : checked && (
                    <svg width="12" height="10" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )
            )}
        </span>
    );
});
Checkbox.displayName = 'Checkbox';

// Shared pill-button row used by both the Filter and Sort popovers below —
// a horizontal row of rounded options with the active one filled solid.
const PillGroup = React.memo(({ options, value, onChange }) => (
    <div style={{ display: "flex", gap: fluid(10), flexWrap: "wrap" }}>
        {options.map((opt) => {
            const isActive = opt.value === value;
            return (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    style={{
                        padding: `${fluid(10)} ${fluid(20)}`,
                        borderRadius: fluid(1000),
                        border: isActive ? "none" : `1px solid #E6E6E6`,
                        backgroundColor: isActive ? COLORS.primary : "#FFFFFF",
                        color: isActive ? "#FFFFFF" : "#636363",
                        fontSize: fluid(14),
                        fontWeight: 500,
                        fontFamily: "Roboto, sans-serif",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}
                >
                    {opt.label}
                </button>
            );
        })}
    </div>
));
PillGroup.displayName = 'PillGroup';

// Divider + RESET/APPLY footer shared by the Filter and Sort popovers.
const PopoverFooter = React.memo(({ onReset, onApply, disabled = false }) => (
    <>
        <div style={{ width: "100%", height: "1px", backgroundColor: COLORS.borderLight, margin: `${fluid(24)} 0` }} />
        <div style={{ display: "flex", justifyContent: "space-between", gap: fluid(12) }}>
            <button
                type="button"
                onClick={disabled ? undefined : onReset}
                disabled={disabled}
                style={{
                    height: fluid(40),
                    // Keep the label centred regardless of page button CSS.
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    boxSizing: "border-box",
                    padding: `0 ${fluid(20)}`,
                    borderRadius: fluid(10),
                    border: `1.5px solid ${COLORS.primary}`,
                    backgroundColor: "transparent",
                    color: COLORS.primary,
                    fontSize: fluid(14),
                    fontWeight: 700,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    transition: "opacity 0.2s ease",
                }}
            >
                RESET
            </button>
            <button
                type="button"
                onClick={disabled ? undefined : onApply}
                disabled={disabled}
                style={{
                    height: fluid(40),
                    // Keep the label centred regardless of page button CSS.
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 1,
                    boxSizing: "border-box",
                    padding: `0 ${fluid(28)}`,
                    borderRadius: fluid(10),
                    border: "none",
                    backgroundColor: COLORS.primary,
                    color: "#FFFFFF",
                    fontSize: fluid(14),
                    fontWeight: 700,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.5 : 1,
                    transition: "opacity 0.2s ease",
                }}
            >
                APPLY
            </button>
        </div>
    </>
));
PopoverFooter.displayName = 'PopoverFooter';

// Manage data (move a teacher's data to someone else) — teachers only, like the old user table.
const isTeacherRow = (row) => String(row?.role || "").toLowerCase() === "teacher";

const RowActionsMenu = React.memo(({ row, onToggleStatus, onDelete, onViewProfile, onEditDetails, onManageData, disabled = false }) => {
    const [open, setOpen] = useState(false);
    // Opens upward when there isn't room below (last rows) — same as StudentListTable.
    const [openUp, setOpenUp] = useState(false);
    const menuRef = useRef(null);

    const toggleMenu = (buttonEl) => {
        if (disabled) return;
        if (open) { setOpen(false); return; }
        // Bottom edge of the nearest scrolling/clipping ancestor (or the window).
        let clipBottom = window.innerHeight;
        for (let node = buttonEl?.parentElement; node && node !== document.body; node = node.parentElement) {
            const { overflowY } = window.getComputedStyle(node);
            if (overflowY !== 'visible') { clipBottom = Math.min(clipBottom, node.getBoundingClientRect().bottom); break; }
        }
        const spaceBelow = clipBottom - (buttonEl?.getBoundingClientRect().bottom || 0);
        // Staff menu can have 4 items (View, Manage data, Suspend, Delete).
        setOpenUp(spaceBelow < 220);
        setOpen(true);
    };

    useEffect(() => {
        if (!open) return undefined;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    // Once this row is checked, the toolbar's Suspend/Delete already acts on
    // it — close and lock its own menu so there isn't a second, conflicting
    // way to act on the row.
    useEffect(() => {
        if (disabled) setOpen(false);
    }, [disabled]);

    const items = [
        {
            label: "View",
            color: "#5A5A5A",
            onClick: () => { onViewProfile?.(row); setOpen(false); },
        },
        ...(isTeacherRow(row) ? [{
            label: "Manage data",
            color: "#594099",
            onClick: () => { onManageData?.(row); setOpen(false); },
        }] : []),
        {
            label: row.status === "Active" ? "Suspend" : "Activate",
            color: row.status === "Active" ? "#D97706" : "#008A67",
            onClick: () => { onToggleStatus(row.id); setOpen(false); },
        },
        {
            label: "Delete",
            color: "#FF0010",
            onClick: () => { onDelete(row.id); setOpen(false); },
        },
    ];

    return (
        <div ref={menuRef} onClick={(e) => e.stopPropagation()} style={{ position: "relative", justifySelf: "end" }}>
            <button
                type="button"
                onClick={(e) => toggleMenu(e.currentTarget)}
                disabled={disabled}
                style={{
                    width: fluid(44),
                    height: fluid(44),
                    border: "none",
                    borderRadius: fluid(8),
                    backgroundColor: open ? "#F1F1F1" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.4 : 1,
                }}
            >
                <FiMoreVertical size={24} color="#666666" strokeWidth={2} />
            </button>

            {open && !disabled && (
                <div style={{
                    position: "absolute",
                    ...(openUp ? { bottom: fluid(48) } : { top: fluid(48) }),
                    right: 0,
                    minWidth: fluid(140),
                    backgroundColor: "#FFFFFF",
                    borderRadius: fluid(16),
                    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                    padding: fluid(8),
                    zIndex: 30,
                }}>
                    {items.map((item) => (
                        <div
                            key={item.label}
                            onClick={item.onClick}
                            title={item.title}
                            className="staff-row-menu-item"
                            style={{
                                display: "flex",
                                alignItems: "center",
                                padding: `${fluid(10)} ${fluid(12)}`,
                                borderRadius: 0,
                                fontSize: fluid(16),
                                fontWeight: 400,
                                color: item.color,
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {item.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});
RowActionsMenu.displayName = 'RowActionsMenu';

const TableRow = React.memo(({ row, checked, onToggleCheck, onToggleStatus, onDelete, onViewProfile, onEditDetails, onManageData, isLast, actionsDisabled }) => (
    <div style={{ position: "relative" }}>
        <div
            style={{
                display: "grid",
                gridTemplateColumns: COLUMN_TEMPLATE,
                alignItems: "center",
                gap: fluid(12),
                minHeight: "72px",
                padding: `0 ${fluid(20)}`,
                boxSizing: "border-box",
            }}
        >
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleCheck(row.id);
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    alignSelf: 'stretch',
                    cursor: 'pointer',
                }}
            >
                <Checkbox
                    checked={checked}
                    onChange={(e) => {
                        if (e && e.stopPropagation) e.stopPropagation();
                        onToggleCheck(row.id);
                    }}
                    ariaLabel={`Select ${row.name}`}
                />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                <Avatar name={row.name} image={row.image} />
                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <span
                        onClick={() => onViewProfile(row)}
                        style={{
                            fontSize: "13px",
                            lineHeight: "24px",
                            fontWeight: 400,
                            color: "#191919",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontFamily: "Roboto, sans-serif",
                            cursor: "pointer",
                        }}
                    >
                        {row.name}
                    </span>
                    <span style={{ fontSize: "13px", lineHeight: "24px", fontWeight: 400, color: "#AEAEAE", whiteSpace: "nowrap", fontFamily: "Roboto, sans-serif" }}>
                        Joined on {formatJoinedDate(row.joinedOn)}
                    </span>
                </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span style={{ fontSize: "13px", lineHeight: "24px", fontWeight: 400, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                    {formatPhoneNumber(row.phone)}
                </span>
                <span style={{ fontSize: "13px", lineHeight: "24px", fontWeight: 400, color: "#AEAEAE", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                    {row.email}
                </span>
            </div>

            <RoleBadge role={row.role} />

            <span style={{ fontSize: "13px", lineHeight: "24px", fontWeight: 400, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontFamily: "Roboto, sans-serif" }}>
                {row.access}
            </span>

            <StatusBadge status={row.status} />

            <RowActionsMenu row={row} onToggleStatus={onToggleStatus} onDelete={onDelete} onViewProfile={onViewProfile} onEditDetails={onEditDetails} onManageData={onManageData} disabled={actionsDisabled} />
        </div>

        {!isLast && (
            <div style={{
                position: "absolute",
                left: fluid(24),
                right: fluid(24),
                bottom: 0,
                height: "1px",
                backgroundColor: COLORS.borderLight,
            }} />
        )}
    </div>
));
TableRow.displayName = 'TableRow';

const GridCard = React.memo(({ row, checked, hovered, onHoverEnter, onHoverLeave, onToggleCheck, onToggleStatus, onDelete, onViewProfile, onManageData, actionsDisabled = false }) => {
    const isOperations = row.role.toLowerCase().includes("operation");
    const cardStyle = {
        width: "100%",
        display: "flex",
        flexDirection: "column",
        padding: fluid(20),
        boxSizing: "border-box",
        backgroundColor: checked ? "#EDE9F6" : "#FFFFFF",
        borderRadius: fluid(24),
        boxShadow: checked ? "none" : "4px 4px 10px 4px rgba(0,0,0,0.04)",
    };

    // The collapsed card always renders in normal flow, so the grid cell's
    // own intrinsic height never changes on hover — that's what keeps the
    // row below from being pushed down. The expanded card is an ADDITIONAL
    // absolutely-positioned overlay drawn on top of it (height: 200% of the
    // cell + one grid gap, matching the two cards it visually sits beside),
    // so it overlaps the row below instead of growing the whole row.
    return (
        <div
            style={{ width: "100%", position: "relative", zIndex: hovered ? 100 : 1 }}
            onMouseEnter={onHoverEnter}
            onMouseLeave={onHoverLeave}
        >
            <div style={cardStyle}>
                <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", minWidth: 0 }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: fluid(12) }}>
                        <div style={{ display: "flex", alignItems: "center", gap: fluid(12), minWidth: 0 }}>
                            <span
                                onClick={() => onViewProfile?.(row)}
                                style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }}
                            >
                                {row.name}
                            </span>
                            {/* Status dot removed from the grid card. */}
                            {/* <StatusDot status={row.status} /> */}
                        </div>
                        <span style={{ display: "block", marginTop: fluid(7), fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, letterSpacing: 0, color: "#636363", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {formatPhoneNumber(row.phone)}
                        </span>
                        <span style={{ display: "block", marginTop: fluid(4), fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, letterSpacing: 0, color: "#636363", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {row.email}
                        </span>
                    </div>
                    <GridAvatar name={row.name} image={row.image} />
                </div>

                <div style={{ width: "100%", minWidth: 0, display: "flex", alignItems: "center", gap: fluid(7), marginTop: fluid(15) }}>
                    <img src={isOperations ? OperationIcon : TeacherIcon} alt={row.role} style={{ width: fluid(24), height: fluid(24), flexShrink: 0 }} />
                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {isOperations ? "Operations" : row.role}
                    </span>
                    <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        ({row.access})
                    </span>
                </div>
            </div>

            {hovered && (
                <div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `calc(200% + ${fluid(24)})`,
                    zIndex: 200,
                    padding: fluid(20),
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: checked ? "#EDE9F6" : "#FFFFFF",
                    borderRadius: fluid(24),
                    boxShadow: checked ? "none" : "0 7px 22px rgba(0,0,0,0.12)",
                    animation: "staffGridCardExpand 0.12s ease-out",
                }}>
                    <div style={{ position: "relative", minHeight: fluid(92), width: "100%", display: "flex", alignItems: "flex-start" }}>
                        <Checkbox checked={checked} onChange={() => onToggleCheck(row.id)} ariaLabel={`Select ${row.name}`} />

                        <div style={{ minWidth: 0, flex: 1, paddingLeft: fluid(18), paddingRight: fluid(8) }}>
                            <div style={{ display: "flex", alignItems: "center", gap: fluid(12), minWidth: 0 }}>
                                <span
                                    onClick={() => onViewProfile?.(row)}
                                    style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#191919", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", cursor: "pointer" }}
                                >
                                    {row.name}
                                </span>
                                {/* Status dot removed from the grid card. */}
                                {/* <StatusDot status={row.status} /> */}
                            </div>
                            <span style={{ display: "block", marginTop: fluid(7), fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, letterSpacing: 0, color: "#636363" }}>
                                {formatPhoneNumber(row.phone)}
                            </span>
                            <span style={{ display: "block", marginTop: fluid(4), fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 400, letterSpacing: 0, color: "#636363", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {row.email}
                            </span>
                            <span style={{ display: "block", marginTop: fluid(10), fontFamily: "Roboto, sans-serif", fontSize: fluid(12), fontWeight: 400, letterSpacing: 0, color: "#AEAEAE" }}>
                                Joined on {formatJoinedDate(row.joinedOn)}
                            </span>
                        </div>

                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: fluid(68), paddingRight: fluid(12), boxSizing: "border-box", flexShrink: 0 }}>
                            <GridAvatar name={row.name} image={row.image} />
                            <span
                                onClick={() => onViewProfile?.(row)}
                                style={{ marginTop: fluid(16), marginLeft: `calc(-1 * ${fluid(16)})`, fontFamily: "Roboto, sans-serif", color: "#1089FF", fontSize: fluid(14), fontWeight: 700, letterSpacing: 0, whiteSpace: "nowrap", cursor: "pointer" }}
                            >
                                VIEW PROFILE
                            </span>
                        </div>
                    </div>

                    <div style={{ marginTop: fluid(30) }}>
                        <div style={{ display: "flex", alignItems: "center", marginTop: fluid(14) }}>
                            <span style={{ paddingRight: fluid(8), fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#AEAEAE" }}>Role:</span>
                            <img src={isOperations ? OperationIcon : TeacherIcon} alt={row.role} style={{ width: fluid(20), height: fluid(20), flexShrink: 0, marginRight: fluid(6) }} />
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#191919" }}>{isOperations ? "Operations" : row.role}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", marginTop: fluid(14) }}>
                            <span style={{ paddingRight: fluid(8), fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#AEAEAE" }}>Access:</span>
                            <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 400, letterSpacing: 0, color: "#191919" }}>{row.access}</span>
                        </div>
                    </div>

                    {/* marginTop auto: buttons sit at the bottom of the hover card. */}
                    <div style={{ marginTop: "auto", paddingTop: fluid(24), display: "flex", alignItems: "center", justifyContent: "space-between", gap: fluid(12) }}>
                        {isTeacherRow(row) && (
                            <button
                                type="button"
                                disabled={actionsDisabled}
                                onClick={() => onManageData?.(row)}
                                style={{ border: "none", background: "transparent", padding: 0, fontSize: fluid(14), fontWeight: 700, cursor: actionsDisabled ? "not-allowed" : "pointer", opacity: actionsDisabled ? 0.4 : 1, color: "#594099" }}
                            >
                                MANAGE DATA
                            </button>
                        )}
                        {/* Disabled while rows are selected — the header's bulk SUSPEND / DELETE apply instead. */}
                        <button
                            type="button"
                            disabled={actionsDisabled}
                            onClick={() => onToggleStatus(row.id)}
                            style={{ border: "none", background: "transparent", padding: 0, fontSize: fluid(14), fontWeight: 700, cursor: actionsDisabled ? "not-allowed" : "pointer", opacity: actionsDisabled ? 0.4 : 1, color: row.status === "Active" ? "#FFA931" : "#008A67" }}
                        >
                            {row.status === "Active" ? "SUSPEND" : "ACTIVATE"}
                        </button>
                        <button
                            type="button"
                            disabled={actionsDisabled}
                            onClick={() => onDelete(row.id)}
                            style={{ border: "none", background: "transparent", padding: 0, fontSize: fluid(14), fontWeight: 700, cursor: actionsDisabled ? "not-allowed" : "pointer", opacity: actionsDisabled ? 0.4 : 1, color: "#FF414D" }}
                        >
                            DELETE
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});
GridCard.displayName = 'GridCard';

const exportRowsToExcel = (rows, label) => {
    if (!rows || rows.length === 0) {
        Notification.info(`No ${label.toLowerCase()} data to export`);
        return;
    }
    const data = rows.map((row) => ({
        "Name": row.name || "—",
        "Phone Number": formatPhoneNumber(row.phone) || "—",
        "Email ID": row.email || "—",
        "Role": row.role ? (row.role.toLowerCase().includes("operation") ? "Operations" : row.role) : "—",
        "Access": row.access || "—",
        "Status": row.status || "—",
        "Joined Date": formatJoinedDate(row.joinedOn),
    }));
    exportToExcel(data, `${label.toLowerCase()}-list`, `${label} List`);
};

const StaffListTable = React.memo(({
    onAddStaff, onOpenRegistrations, onViewProfile, onEditDetails, onAddAccess, registrationsCount = 0, isMobile = false, activeTab = "STAFF",
    staffList, lastAddedStaff, staffStats, getStaffListV3, searchStaffV3, getStaffStats, updateStaffStatusV3, bulkUpdateStaffStatus, setUserDetails,
    onStaffStatusChange, updateUserToTranferData,
}) => {
    const history = useHistory();
    const location = useLocation();
    const basePath = location.pathname.startsWith('/user-management-v2') ? '/user-management-v2' : '/user-management';
    const deletedIdFromRoute = location?.state?.deletedStaffId || location?.state?.deletedUserId;
    const isStaff = activeTab === "STAFF";
    const [studentRows, setStudentRows] = useState(STUDENT_ROWS);
    const [staffItems, setStaffItems] = useState([]);
    const allStaffCacheRef = useRef([]);
    // Which search / status / role `staffItems` was last fetched for — while it
    // differs from the current view, rows come from the cache instead.
    const [itemsQuery, setItemsQuery] = useState(null);
    // True once the cache also holds inactive staff (background fetch below).
    const hasInactiveCacheRef = useRef(false);
    const [cacheVersion, setCacheVersion] = useState(0);
    const prevLastAddedStaffRef = useRef(lastAddedStaff);
    const deletedIdsRef = useRef(new Set(deletedIdFromRoute ? [deletedIdFromRoute] : []));
    const [isLoading, setIsLoading] = useState(false);
    // False until this mount's first list request answers — until then the
    // rows area shows a loader instead of an old (redux) copy of the list.
    // Searching / filtering later never brings it back.
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    // First-load spinner area stretches to the window bottom (level with the sidebar).
    const initialLoaderRef = useRef(null);
    const initialLoaderHeight = useFillToViewport(initialLoaderRef, isStaff && !initialLoadDone);
    const knownTotalRef = useRef(0);
    const activeRequestIdRef = useRef(0);
    const fetchStaffRef = useRef(null);
    // Search / filter / sort applied before opening a profile (restored on return).
    const viewStateKey = `staff:${activeTab}`;
    const viewStateKeyRef = useRef(viewStateKey);
    viewStateKeyRef.current = viewStateKey;
    const [restoredView] = useState(() => getListViewState(viewStateKey));
    const [search, setSearch] = useState(restoredView?.search ?? "");
    const [statusFilter, setStatusFilter] = useState(restoredView?.statusFilter ?? "Active");
    const [accessFilter, setAccessFilter] = useState(restoredView?.accessFilter ?? "All");
    const [roleFilter, setRoleFilter] = useState(restoredView?.roleFilter ?? "All");
    const [page, setPage] = useState(1);
    // Rows per page — picked from the "1-20 of N" range text.
    const [pageLimit, setPageLimit] = useState(20);
    // 'newest' matches the order getStaffListV3 already returns (newest
    // created first), so it's the default without needing an extra sort pass.
    const [sortBy, setSortBy] = useState(restoredView?.sortBy ?? "newest");
    // List / grid view — restored on return from a profile like the filters.
    const [viewMode, setViewMode] = useState(restoredView?.viewMode ?? "list");
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [downloadModalOpen, setDownloadModalOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [sortOpen, setSortOpen] = useState(false);
    // Draft copies so Role/Status/Sort selections only take effect once the
    // popover's APPLY is clicked, matching the confirm/reset footer design —
    // not live-filtering on every pill click.
    const [draftRoleFilter, setDraftRoleFilter] = useState(roleFilter);
    const [draftStatusFilter, setDraftStatusFilter] = useState(statusFilter);
    const [draftSortBy, setDraftSortBy] = useState(sortBy);

    useEffect(() => {
        saveListViewState(viewStateKeyRef.current, { search, statusFilter, accessFilter, roleFilter, sortBy, viewMode });
    }, [search, statusFilter, accessFilter, roleFilter, sortBy, viewMode]);

    // Keep the saved view only when leaving for a profile page.
    useEffect(() => () => {
        if (!isProfilePath(history.location?.pathname)) clearListViewState(viewStateKeyRef.current);
    }, [history]);
    const [hoveredCardId, setHoveredCardId] = useState(null);
    const filterRef = useRef(null);
    const sortRef = useRef(null);
    // Open Filter/Sort popover panels — scrolling with the cursor over them
    // must not scroll the table behind (see useBlockScrollThrough).
    const filterPanelRef = useRef(null);
    const sortPanelRef = useRef(null);
    useBlockScrollThrough(filterPanelRef, filterOpen);
    useBlockScrollThrough(sortPanelRef, sortOpen);
    const tableRootRef = useRef(null);

    // Search/Filter/Sort should feel like the table is "in focus" — if the
    // page is scrolled down into the analytics cards above, glide the table
    // back up to the same pinned position it settles into on manual scroll
    // (its toolbar is position: sticky; top: 0 within the outer scroller).
    // Returns a promise that resolves once the scroll has settled (or
    // immediately if it was already at the top) — opening the Filter/Sort
    // popover right away mid-scroll was what made it look like it "jumped":
    // the popover's height gets added to the page while scrollIntoView is
    // still animating, so the target position shifts under it. Awaiting
    // this first lets the scroll finish before the popover's layout change happens.
    const scrollTableIntoView = useCallback(() => {
        const el = tableRootRef.current;
        if (!el) return Promise.resolve();
        const { top } = el.getBoundingClientRect();
        if (Math.abs(top) < 4) return Promise.resolve();
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return new Promise((resolve) => setTimeout(resolve, 450));
    }, []);

    const rowsRegionRef = useRef(null);

    // Changing page / rows per page: start the new page from its first row.
    const skipFirstPageScrollRef = useRef(true);
    useEffect(() => {
        if (skipFirstPageScrollRef.current) { skipFirstPageScrollRef.current = false; return; }
        scrollToTableTop(tableRootRef.current, rowsRegionRef.current);
    }, [page, pageLimit]);

    useEffect(() => {
        if (deletedIdFromRoute) {
            deletedIdsRef.current.add(deletedIdFromRoute);
            setStaffItems((prev) => prev.filter((item) => (item.id !== deletedIdFromRoute && item.userId !== deletedIdFromRoute)));
            if (allStaffCacheRef.current.length > 0) {
                allStaffCacheRef.current = allStaffCacheRef.current.filter((item) => (item.id !== deletedIdFromRoute && item.userId !== deletedIdFromRoute));
            }
            knownTotalRef.current = Math.max(0, knownTotalRef.current - 1);
            fetchStaffRef.current?.();
        }
    }, [deletedIdFromRoute]);

    const extractItemsFromResponse = (res) => {
        if (!res) return [];
        if (Array.isArray(res?.data)) return res.data;
        if (Array.isArray(res?.data?.data)) return res.data.data;
        if (Array.isArray(res?.items)) return res.items;
        if (Array.isArray(res)) return res;
        return [];
    };

    const fetchStaff = useCallback((callback, showLoader = false) => {
        if (!isStaff) return;
        const trimmed = search.trim();

        const params = { page: 1, limit: 1000 };
        if (roleFilter !== "All") params.role = roleFilter;
        if (statusFilter !== "All") params.status = toApiStatus(statusFilter);
        if (trimmed) {
            params.search = trimmed;
        }
        if (sortBy === "alphabetic") {
            params.sort = "alphabetic";
            params.sortBy = "alphabetic";
        } else if (sortBy === "oldest") {
            params.sort = "oldest";
            params.sortBy = "oldest";
        } else {
            params.sort = "newest";
            params.sortBy = "newest";
        }

        const requestId = ++activeRequestIdRef.current;
        // Never apply loader while searching, filtering, or sorting — show data directly
        const isSearchOrFilterOrSort = Boolean(trimmed || roleFilter !== "All" || statusFilter !== "Active" || sortBy !== "newest");
        // Only the default view is stored in redux — otherwise coming back to
        // Users V2 (or switching tabs) would first show the old search/filter result.
        if (isSearchOrFilterOrSort) params.skipRedux = true;
        if (showLoader && !isSearchOrFilterOrSort && staffItems.length === 0) {
            setIsLoading(true);
        }
        const safety = setTimeout(() => {
            if (requestId === activeRequestIdRef.current) {
                setIsLoading(false);
            }
        }, 5000);
        getStaffListV3?.(params, (response, error) => {
            if (requestId !== activeRequestIdRef.current) {
                return;
            }
            if (error || !response) {
                clearTimeout(safety);
                setIsLoading(false);
                setInitialLoadDone(true);
                callback?.();
                return;
            }
            const list = extractItemsFromResponse(response);
            const total = Number(response?.total ?? response?.totalItems ?? 0);

            // Filter out any known deleted staff IDs
            const sanitizedList = list.filter((item) => {
                const id = item.id ?? item.userId;
                return !deletedIdsRef.current.has(id);
            });

            // Preserve newly added staff item if not yet indexed by server, and preserve its avatar if not yet in API response
            const newlyAddedRaw = lastAddedStaff?.data ?? (lastAddedStaff?.id ? lastAddedStaff : null);
            const newlyAddedId = newlyAddedRaw?.id ?? newlyAddedRaw?.userId;
            const mappedList = sanitizedList.map((item) => {
                const id = item.id ?? item.userId;
                if (id && newlyAddedId && id === newlyAddedId) {
                    const currentImg = item?.avatar || item?.image || item?.basicprofile?.image || item?.basicprofile?.avatar;
                    const fallbackImg = newlyAddedRaw?.image || newlyAddedRaw?.avatar;
                    if (!currentImg && fallbackImg) {
                        return {
                            ...item,
                            image: fallbackImg,
                            avatar: fallbackImg,
                        };
                    }
                }
                return item;
            });
            const unindexed = (newlyAddedId && !deletedIdsRef.current.has(newlyAddedId) && !mappedList.some((item) => (item.id === newlyAddedId || item.userId === newlyAddedId)))
                ? staffItems.filter((item) => (item.id === newlyAddedId || item.userId === newlyAddedId))
                : [];
            const finalList = unindexed.length > 0 ? [...unindexed, ...mappedList] : mappedList;
            const resolvedTotal = total > 0 ? total : finalList.length;
            if (resolvedTotal > 0) {
                knownTotalRef.current = resolvedTotal;
            }

            // If backend returned fewer items than total and capped limit, fetch remaining pages to ensure
            // the full dataset is available for complete multi-page sorting and pagination.
            if (sanitizedList.length < total && sanitizedList.length > 0) {
                const pageSize = sanitizedList.length;
                const totalPagesCount = Math.ceil(total / pageSize);
                const pagePromises = [];
                for (let p = 2; p <= totalPagesCount; p++) {
                    pagePromises.push(
                        new Promise((resolve) => {
                            getStaffListV3?.({ ...params, page: p, limit: pageSize }, (pageRes) => {
                                resolve(extractItemsFromResponse(pageRes));
                            });
                        })
                    );
                }
                Promise.all(pagePromises).then((results) => {
                    if (requestId !== activeRequestIdRef.current) return;
                    clearTimeout(safety);
                    setIsLoading(false);
                    setInitialLoadDone(true);
                    const sanitizedResults = results.flat().filter((item) => {
                        const id = item.id ?? item.userId;
                        return !deletedIdsRef.current.has(id);
                    });
                    const combined = [...finalList, ...sanitizedResults];
                    const mappedCombined = combined.map((item) => {
                        const id = item.id ?? item.userId;
                        if (id && newlyAddedId && id === newlyAddedId) {
                            const currentImg = item?.avatar || item?.image || item?.basicprofile?.image || item?.basicprofile?.avatar;
                            const fallbackImg = newlyAddedRaw?.image || newlyAddedRaw?.avatar;
                            if (!currentImg && fallbackImg) {
                                return {
                                    ...item,
                                    image: fallbackImg,
                                    avatar: fallbackImg,
                                };
                            }
                        }
                        return item;
                    });
                    const missingCombined = (newlyAddedId && !deletedIdsRef.current.has(newlyAddedId) && !mappedCombined.some((item) => (item.id === newlyAddedId || item.userId === newlyAddedId)))
                        ? staffItems.filter((item) => (item.id === newlyAddedId || item.userId === newlyAddedId))
                        : [];
                    const finalCombined = missingCombined.length > 0 ? [...missingCombined, ...mappedCombined] : mappedCombined;
                    setStaffItems(finalCombined);
                    setItemsQuery({ search: trimmed, status: statusFilter, role: roleFilter });
                    if (!trimmed && roleFilter === "All" && (finalCombined.length > 0 || statusFilter !== "Active")) {
                        mergeStaffCache(allStaffCacheRef, finalCombined, statusFilter);
                        if (statusFilter !== "Active") hasInactiveCacheRef.current = true;
                    }
                    callback?.();
                }).catch(() => {
                    if (requestId !== activeRequestIdRef.current) return;
                    clearTimeout(safety);
                    setIsLoading(false);
                    setInitialLoadDone(true);
                    setStaffItems(finalList);
                    setItemsQuery({ search: trimmed, status: statusFilter, role: roleFilter });
                    if (!trimmed && roleFilter === "All" && (finalList.length > 0 || statusFilter !== "Active")) {
                        mergeStaffCache(allStaffCacheRef, finalList, statusFilter);
                        if (statusFilter !== "Active") hasInactiveCacheRef.current = true;
                    }
                    callback?.();
                });
            } else {
                clearTimeout(safety);
                setIsLoading(false);
                setInitialLoadDone(true);
                setStaffItems(finalList);
                setItemsQuery({ search: trimmed, status: statusFilter, role: roleFilter });
                if (!trimmed && roleFilter === "All" && (finalList.length > 0 || statusFilter !== "Active")) {
                    mergeStaffCache(allStaffCacheRef, finalList, statusFilter);
                    if (statusFilter !== "Active") hasInactiveCacheRef.current = true;
                }
                callback?.();
            }
        });
    }, [isStaff, search, roleFilter, statusFilter, sortBy, getStaffListV3, lastAddedStaff]);

    useEffect(() => {
        fetchStaffRef.current = fetchStaff;
    }, [fetchStaff]);

    // Load every staff member (all statuses — same request as the "All"
    // filter) into the cache once, so clearing a search or changing the
    // status filter shows the full list immediately instead of an old search
    // result / "no data" first. Direct Api call, not redux, so it never
    // replaces the visible list.
    useEffect(() => {
        if (!isStaff) return undefined;
        let cancelled = false;
        Api.get("/userv2/staff")
            .params({ page: 1, limit: 1000, sort: "newest", sortBy: "newest" })
            .send((response) => {
                if (cancelled || !response) return;
                const list = extractItemsFromResponse(response).filter((item) => !deletedIdsRef.current.has(staffIdOf(item)));
                mergeStaffCache(allStaffCacheRef, list, "All");
                hasInactiveCacheRef.current = true;
                setCacheVersion((v) => v + 1);
            }, true);
        return () => { cancelled = true; };
    }, [isStaff]);

    const refetchStaffList = useCallback((callback) => {
        fetchStaffRef.current?.(callback);
    }, []);

    useEffect(() => {
        const total = Number(staffList?.meta?.totalItems || 0);
        if (total > 0) {
            knownTotalRef.current = total;
        }
    }, [staffList?.meta?.totalItems]);

    useEffect(() => {
        if (!isStaff) return;
        if (page !== 1) {
            setPage(1);
        }
    }, [isStaff, search, roleFilter, statusFilter, sortBy]);

    useEffect(() => {
        if (!isStaff) return;
        setIsLoading(false);
        const timer = setTimeout(() => {
            fetchStaffRef.current?.(undefined, false);
        }, search.trim() ? 300 : 0);
        return () => clearTimeout(timer);
    }, [isStaff, search, roleFilter, statusFilter, sortBy]);

    useEffect(() => {
        if (!isStaff) return;
        if (Array.isArray(staffList?.items)) {
            const apiTotal = Number(staffList?.meta?.totalItems || 0);
            if (apiTotal > 0) {
                knownTotalRef.current = apiTotal;
            }
            // Redux staffList holds the result of the LAST getStaffListV3 call —
            // including search / filter fetches — so it's only used to fill the
            // table before this mount's own fetch lands. After that fetchStaff
            // sets the rows; copying staffList here would put an old search /
            // filter result back (and into the full-list cache).
            if (!search.trim() && !itemsQuery) {
                const newlyAddedRaw = lastAddedStaff?.data ?? (lastAddedStaff?.id ? lastAddedStaff : null);
                const newlyAddedId = newlyAddedRaw?.id ?? newlyAddedRaw?.userId;
                const activeItems = staffList.items.filter((item) => {
                    const id = item.id ?? item.userId;
                    return !deletedIdsRef.current.has(id);
                }).map((item) => {
                    const id = item.id ?? item.userId;
                    if (id && newlyAddedId && id === newlyAddedId) {
                        const currentImg = item?.avatar || item?.image || item?.basicprofile?.image || item?.basicprofile?.avatar;
                        const fallbackImg = newlyAddedRaw?.image || newlyAddedRaw?.avatar;
                        if (!currentImg && fallbackImg) {
                            return {
                                ...item,
                                image: fallbackImg,
                                avatar: fallbackImg,
                            };
                        }
                    }
                    return item;
                });
                const unindexedNew = (newlyAddedId && !deletedIdsRef.current.has(newlyAddedId) && !activeItems.some((item) => (item.id === newlyAddedId || item.userId === newlyAddedId)))
                    ? staffItems.filter((item) => (item.id === newlyAddedId || item.userId === newlyAddedId))
                    : [];
                const itemsToSet = unindexedNew.length > 0 ? [...unindexedNew, ...activeItems] : activeItems;
                if (itemsToSet.length > 0) {
                    setStaffItems(itemsToSet);
                    if (roleFilter === "All" && statusFilter === "Active") {
                        mergeStaffCache(allStaffCacheRef, itemsToSet, "Active");
                    }
                } else if (allStaffCacheRef.current.length > 0) {
                    setStaffItems(allStaffCacheRef.current);
                } else {
                    setStaffItems([]);
                }
            }
        }
    }, [isStaff, staffList, search, lastAddedStaff, roleFilter, statusFilter, itemsQuery]);

    useEffect(() => {
        if (!isStaff) return;
        if (lastAddedStaff && lastAddedStaff !== prevLastAddedStaffRef.current) {
            prevLastAddedStaffRef.current = lastAddedStaff;
            const rawStaffData = lastAddedStaff?.data ?? (lastAddedStaff?.id ? lastAddedStaff : null);
            if (rawStaffData) {
                const newStaffData = {
                    ...rawStaffData,
                    createdAt: rawStaffData.createdAt || new Date().toISOString(),
                };
                const id = newStaffData.id ?? newStaffData.userId;
                const alreadyExists = staffItems.some((item) => (item.id && item.id === id) || (item.userId && item.userId === id));
                setStaffItems((prev) => {
                    const exists = prev.some((item) => (item.id && item.id === id) || (item.userId && item.userId === id));
                    if (!exists) {
                        return [newStaffData, ...prev];
                    }
                    return prev.map((item) => {
                        if ((item.id && item.id === id) || (item.userId && item.userId === id)) {
                            return {
                                ...item,
                                ...newStaffData,
                                image: newStaffData.image || newStaffData.avatar || item.image || item.avatar,
                                avatar: newStaffData.avatar || newStaffData.image || item.avatar || item.image,
                            };
                        }
                        return item;
                    });
                });
                if (allStaffCacheRef.current.length > 0) {
                    const existsInCache = allStaffCacheRef.current.some((item) => (item.id && item.id === id) || (item.userId && item.userId === id));
                    if (!existsInCache) {
                        allStaffCacheRef.current = [newStaffData, ...allStaffCacheRef.current];
                    }
                }
                if (!alreadyExists) {
                    knownTotalRef.current = Math.max(knownTotalRef.current + 1, (staffList?.meta?.totalItems || 0) + 1);
                }
            }
            setPage(1);
            fetchStaffRef.current?.();
        }
    }, [isStaff, lastAddedStaff, staffList?.meta?.totalItems]);

    const normalizedStaffRows = useMemo(() => {
        // staffItems still holds an older search / filter result (API for the
        // current view in flight) — show the cache, filtered locally below.
        const isPending = !itemsQuery
            || itemsQuery.search !== search.trim()
            || itemsQuery.status !== statusFilter
            || itemsQuery.role !== roleFilter;
        const cacheCoversStatus = statusFilter === "Active" || hasInactiveCacheRef.current;
        const useCache = isPending && cacheCoversStatus && allStaffCacheRef.current.length > 0;
        const base = useCache
            ? allStaffCacheRef.current
            // API result for the current search/filter is in — show it as
            // returned, even when empty (no falling back to the cached list).
            : (!isPending
                ? staffItems
                : staffItems.length > 0
                ? staffItems
                : (!search.trim() && allStaffCacheRef.current.length > 0
                    ? allStaffCacheRef.current
                    : (Array.isArray(staffList?.items) && staffList.items.length > 0 ? staffList.items : [])));
        const mapped = (Array.isArray(base) ? base : []).map(mapApiRecordToRow);
        return mapped.filter((row) => isKnownStaffRole(row.role) && !deletedIdsRef.current.has(row.id));
    }, [staffItems, staffList, search, itemsQuery, statusFilter, roleFilter, cacheVersion]);

    // Every staff member (all statuses, from the background cache) — "Download all staff".
    const allStaffRows = useMemo(() => allStaffCacheRef.current
        .map(mapApiRecordToRow)
        .filter((row) => isKnownStaffRole(row.role) && !deletedIdsRef.current.has(row.id)), [cacheVersion]);

    const rows = isStaff ? normalizedStaffRows : studentRows;
    const setRows = setStudentRows;
    const label = isStaff ? "Staff" : "Students";

    const isFilterDisabled = roleFilter === "All" && statusFilter === "Active" && draftRoleFilter === "All" && draftStatusFilter === "Active";
    const isSortDisabled = sortBy === "newest" && draftSortBy === "newest";

    useEffect(() => {
        if (!filterOpen) return undefined;
        const handleClickOutside = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setDraftRoleFilter(roleFilter);
                setDraftStatusFilter(statusFilter);
                setFilterOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [filterOpen, roleFilter, statusFilter]);

    useEffect(() => {
        if (!sortOpen) return undefined;
        const handleClickOutside = (e) => {
            if (sortRef.current && !sortRef.current.contains(e.target)) {
                setDraftSortBy(sortBy);
                setSortOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [sortOpen, sortBy]);

    const openFilterPopover = useCallback(() => {
        setDraftRoleFilter(roleFilter);
        setDraftStatusFilter(statusFilter);
        setFilterOpen((o) => !o);
    }, [roleFilter, statusFilter]);

    const applyFilterPopover = useCallback(() => {
        setIsLoading(false);
        setRoleFilter(draftRoleFilter);
        setStatusFilter(draftStatusFilter);
        setPage(1);
        setFilterOpen(false);
    }, [draftRoleFilter, draftStatusFilter]);

    const resetFilterPopover = useCallback(() => {
        setIsLoading(false);
        setDraftRoleFilter("All");
        setDraftStatusFilter("Active");
        setRoleFilter("All");
        setStatusFilter("Active");
        setPage(1);
        setFilterOpen(false);
    }, []);

    const openSortPopover = useCallback(() => {
        setDraftSortBy(sortBy);
        setSortOpen((o) => !o);
    }, [sortBy]);

    const applySortPopover = useCallback(() => {
        setIsLoading(false);
        setSortBy(draftSortBy);
        setPage(1);
        setSortOpen(false);
    }, [draftSortBy]);

    const resetSortPopover = useCallback(() => {
        setIsLoading(false);
        setDraftSortBy("newest");
        setSortBy("newest");
        setPage(1);
        setSortOpen(false);
    }, []);

    const isFirstTabEffectRef = useRef(true);
    useEffect(() => {
        // First run is the mount — keep the restored search / filters.
        if (!isFirstTabEffectRef.current) {
            setSearch("");
            setStatusFilter("Active");
            setAccessFilter("All");
            setRoleFilter("All");
        }
        isFirstTabEffectRef.current = false;
        setSelectedIds(new Set());
        setPage(1);
        if (activeTab === "STAFF" && allStaffCacheRef.current.length > 0) {
            setStaffItems(allStaffCacheRef.current);
        } else {
            setStaffItems([]);
        }
    }, [activeTab]);

    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        const next = rows.filter((row) => {
            const matchesQuery = !query
                || (row.name && row.name.toLowerCase().includes(query))
                || (row.phone && row.phone.toLowerCase().includes(query))
                || (row.email && row.email.toLowerCase().includes(query));
            const matchesStatus = statusFilter === "All" || row.status === statusFilter;
            const matchesAccess = accessFilter === "All" || row.access === accessFilter;
            const matchesRole = roleFilter === "All"
                || row.role === roleFilter
                || (roleFilter === "Operation" && String(row.role || "").toLowerCase().includes("operation"));
            return matchesQuery && matchesStatus && matchesAccess && matchesRole;
        });
        if (sortBy === "alphabetic") {
            return [...next].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        }
        if (sortBy === "oldest") {
            return [...next].sort((a, b) => new Date(a.joinedOn) - new Date(b.joinedOn));
        }
        // "newest" — matches the order getStaffListV3 already returns, but
        // sorted explicitly here too so it stays correct after search/filter
        // narrows the list down.
        return [...next].sort((a, b) => new Date(b.joinedOn) - new Date(a.joinedOn));
    }, [rows, search, statusFilter, accessFilter, roleFilter, sortBy]);

    const hasActiveFilters = Boolean(search.trim() || roleFilter !== "All" || statusFilter !== "Active" || accessFilter !== "All");
    const totalRecords = isStaff
        ? (hasActiveFilters
            ? filteredRows.length
            // Count what the list shows (Active by default), like the student list —
            // not staffStats' total, which also counts inactive staff.
            : (filteredRows.length > 0
                ? filteredRows.length
                : (knownTotalRef.current > 0 ? knownTotalRef.current : Number(staffList?.meta?.totalItems || 0))))
        : (hasActiveFilters ? filteredRows.length : (filteredRows.length > 0 ? filteredRows.length : TOTAL_RECORDS.STUDENTS));
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageLimit));

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    const displayedRows = useMemo(() => {
        const start = (page - 1) * pageLimit;
        return filteredRows.slice(start, start + pageLimit);
    }, [filteredRows, page, pageLimit]);

    const allChecked = displayedRows.length > 0 && displayedRows.every((row) => selectedIds.has(row.id));
    // "–" when some of this page is selected, or rows on other pages are.
    const someChecked = !allChecked && (displayedRows.some((row) => selectedIds.has(row.id)) || selectedIds.size > 0);

    const toggleCheck = useCallback((id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const toggleAll = useCallback(() => {
        if (displayedRows.length === 0) return;
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (allChecked) {
                displayedRows.forEach((row) => next.delete(row.id));
            } else {
                displayedRows.forEach((row) => next.add(row.id));
            }
            return next;
        });
    }, [allChecked, displayedRows]);

    const toggleStatus = useCallback((id, callback) => {
        if (isStaff && updateStaffStatusV3) {
            const currentRow = rows.find((row) => row.id === id);
            const nextApiStatus = currentRow?.status === "Active" ? "Suspended" : "Approved";
            // Row is only updated once the API succeeds, together with the
            // confirm modal closing — not while the modal still shows
            // "SUSPENDING..." / "ACTIVATING...".
            updateStaffStatusV3(id, nextApiStatus, (response, error) => {
                if (!error) {
                    setStaffItems((prev) => prev.map((item) => {
                        if ((item.id && item.id === id) || (item.userId && item.userId === id)) {
                            return { ...item, status: nextApiStatus, user_status: nextApiStatus };
                        }
                        return item;
                    }));
                    if (allStaffCacheRef.current.length > 0) {
                        allStaffCacheRef.current = allStaffCacheRef.current.map((item) => {
                            if ((item.id && item.id === id) || (item.userId && item.userId === id)) {
                                return { ...item, status: nextApiStatus, user_status: nextApiStatus };
                            }
                            return item;
                        });
                    }
                }
                callback?.(response, error);
                refetchStaffList();
                getStaffStats?.();
                onStaffStatusChange?.();
            });
            return;
        }
        setRows((prev) => prev.map((row) => (
            row.id === id ? { ...row, status: row.status === "Active" ? "Inactive" : "Active" } : row
        )));
        callback?.();
    }, [isStaff, rows, updateStaffStatusV3, refetchStaffList, getStaffStats, onStaffStatusChange, setRows]);

    // Delete goes through the same status endpoint as suspend/activate
    // (PATCH /userv2/staff/:userId/status { status: "Deleted" }) — the
    // server enforces delete guards. Students have no delete API, so they
    // keep the old local-only removal.
    const deleteRow = useCallback((id, callback) => {
        if (!id) {
            callback?.();
            return;
        }
        const removeLocally = () => {
            deletedIdsRef.current.add(id);
            setStaffItems((prev) => prev.filter((item) => (item.id !== id && item.userId !== id)));
            if (allStaffCacheRef.current.length > 0) {
                allStaffCacheRef.current = allStaffCacheRef.current.filter((item) => (item.id !== id && item.userId !== id));
            }
            setStudentRows((prev) => prev.filter((row) => row.id !== id));
            knownTotalRef.current = Math.max(0, knownTotalRef.current - 1);

            setSelectedIds((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        };

        if (isStaff && updateStaffStatusV3) {
            // Row is only removed once the API succeeds, together with the
            // confirm modal closing; on failure it stays in the list.
            updateStaffStatusV3(id, "Deleted", (response, error) => {
                if (!error) removeLocally();
                callback?.(response, error);
                refetchStaffList();
                getStaffStats?.();
                onStaffStatusChange?.();
            });
            return;
        }
        removeLocally();
        callback?.();
    }, [isStaff, updateStaffStatusV3, refetchStaffList, getStaffStats, onStaffStatusChange, setStudentRows]);

    // Bulk suspend/delete for the toolbar's selection bar below — sets an
    // explicit status on every selected row (not a per-row toggle like
    // toggleStatus, since a mixed selection has no single "next" status).
    // One PATCH for the whole selection via bulkUpdateStaffStatus rather than
    // firing updateStaffStatusV3 per id — the endpoint reports partial
    // failures itself (e.g. a super admin in the selection can't be
    // archived), so those need surfacing instead of silently refetching.
    const bulkSetStatus = useCallback((ids, apiStatus, callback) => {
        if (!ids.length) {
            callback?.();
            return;
        }
        const removeLocally = (idsToRemove) => {
            if (!idsToRemove.length) return;
            const idSet = new Set(idsToRemove);
            idsToRemove.forEach((id) => deletedIdsRef.current.add(id));
            setStaffItems((prev) => prev.filter((item) => !idSet.has(item.id) && !idSet.has(item.userId)));
            if (allStaffCacheRef.current.length > 0) {
                allStaffCacheRef.current = allStaffCacheRef.current.filter((item) => !idSet.has(item.id) && !idSet.has(item.userId));
            }
            setStudentRows((prev) => prev.filter((row) => !idSet.has(row.id)));
            knownTotalRef.current = Math.max(0, knownTotalRef.current - idsToRemove.length);
        };
        if (isStaff && bulkUpdateStaffStatus) {
            // Rows are only removed / selection cleared once the API
            // responds, together with the confirm modal closing — and only
            // the users that actually succeeded are removed.
            bulkUpdateStaffStatus(ids, apiStatus, (response, error) => {
                if (error) {
                    const errorMsg = (typeof error === 'string' ? error : (error?.message || error?.error)) || (apiStatus === "Deleted" ? "Failed to delete user." : apiStatus === "Approved" ? "Failed to activate user." : "Failed to suspend user.");
                    Notification.error("Error", errorMsg);
                } else {
                    const resData = response?.data ?? response;
                    const failed = resData?.failed || [];
                    const succeeded = resData?.succeeded || [];

                    if (failed.length > 0 && succeeded.length === 0) {
                        Notification.error(
                            "Error",
                            failed.map((f) => f.reason || (f.userId ? `User ${f.userId} failed` : "Action failed")).join(' | ') || (apiStatus === "Deleted" ? "Failed to delete user." : apiStatus === "Approved" ? "Failed to activate user." : "Failed to suspend user.")
                        );
                    } else {
                        if (apiStatus === "Deleted") {
                            const failedIds = new Set(failed.map((f) => f.userId));
                            removeLocally(ids.filter((id) => !failedIds.has(id)));
                        } else {
                            const failedIds = new Set(failed.map((f) => f.userId));
                            const successfulIds = ids.filter((id) => !failedIds.has(id));
                            const succIdSet = new Set(successfulIds);
                            setStaffItems((prev) => prev.map((item) => {
                                const id = item.id ?? item.userId;
                                return succIdSet.has(id) ? { ...item, status: apiStatus, user_status: apiStatus } : item;
                            }));
                            if (allStaffCacheRef.current.length > 0) {
                                allStaffCacheRef.current = allStaffCacheRef.current.map((item) => {
                                    const id = item.id ?? item.userId;
                                    return succIdSet.has(id) ? { ...item, status: apiStatus, user_status: apiStatus } : item;
                                });
                            }
                        }
                        if (failed.length > 0) {
                            Notification.warning(
                                "Some users could not be updated",
                                failed.map((f) => `User ${f.userId}: ${f.reason}`).join(' | ')
                            );
                        }
                        const hasApiNotification = Boolean(response?.show || response?.data?.show || resData?.show);
                        if (!hasApiNotification) {
                            if (apiStatus === "Deleted") {
                                Notification.success("Success", "User deleted successfully.");
                            } else if (apiStatus === "Suspended") {
                                Notification.success("Success", "User suspended successfully.");
                            } else if (apiStatus === "Approved") {
                                Notification.success("Success", "User activated successfully.");
                            }
                        }
                    }
                }
                setSelectedIds(new Set());
                callback?.(response, error);
                refetchStaffList();
                getStaffStats?.();
                onStaffStatusChange?.();
            });
            return;
        } else if (apiStatus === "Deleted") {
            removeLocally(ids);
            setRows((prev) => prev.filter((row) => !ids.includes(row.id)));
            Notification.success("Success", "User deleted successfully.");
            callback?.();
        } else {
            setRows((prev) => prev.map((row) => (
                ids.includes(row.id) ? { ...row, status: apiStatus === "Active" || apiStatus === "Approved" ? "Active" : "Inactive" } : row
            )));
            if (apiStatus === "Suspended") {
                Notification.success("Success", "User suspended successfully.");
            } else if (apiStatus === "Approved" || apiStatus === "Active") {
                Notification.success("Success", "User activated successfully.");
            }
            callback?.();
        }
        setSelectedIds(new Set());
    }, [isStaff, bulkUpdateStaffStatus, refetchStaffList, getStaffStats, onStaffStatusChange, setRows, setStudentRows]);

    const bulkSuspend = useCallback((callback) => {
        bulkSetStatus(Array.from(selectedIds), "Suspended", callback);
    }, [selectedIds, bulkSetStatus]);

    const bulkActivate = useCallback((callback) => {
        bulkSetStatus(Array.from(selectedIds), "Approved", callback);
    }, [selectedIds, bulkSetStatus]);

    const bulkDelete = useCallback((callback) => {
        bulkSetStatus(Array.from(selectedIds), "Deleted", callback);
    }, [selectedIds, bulkSetStatus]);

    // Suspend/activate/delete (single row or bulk) all now route through
    // UserActionConfirmModal instead of firing immediately — these `request*`
    // functions just open it with the right action + scope; the actual
    // toggleStatus/deleteRow/bulkSuspend/bulkDelete call only happens once
    // the user hits the modal's confirm button.
    const [confirmAction, setConfirmAction] = useState(null); // 'suspend' | 'activate' | 'delete'
    const [confirmScope, setConfirmScope] = useState(null); // { type: 'row', id } | { type: 'bulk' }
    const [confirmActionLoading, setConfirmActionLoading] = useState(false);

    const requestToggleStatus = useCallback((id) => {
        const currentRow = rows.find((row) => row.id === id);
        setConfirmAction(currentRow?.status === "Active" ? "suspend" : "activate");
        setConfirmScope({ type: "row", id });
    }, [rows]);

    const requestDeleteRow = useCallback((id) => {
        setConfirmAction("delete");
        setConfirmScope({ type: "row", id });
    }, []);

    const requestBulkDelete = useCallback(() => {
        setConfirmAction("delete");
        setConfirmScope({ type: "bulk" });
    }, []);

    const requestBulkSuspend = useCallback(() => {
        setConfirmAction("suspend");
        setConfirmScope({ type: "bulk" });
    }, []);

    const requestBulkActivate = useCallback(() => {
        setConfirmAction("activate");
        setConfirmScope({ type: "bulk" });
    }, []);

    const closeConfirmModal = useCallback(() => {
        if (confirmActionLoading) return;
        setConfirmAction(null);
        setConfirmScope(null);
    }, [confirmActionLoading]);

    const handleConfirmAction = useCallback(() => {
        if (!confirmScope || confirmActionLoading) return;
        setConfirmActionLoading(true);

        const onDone = () => {
            setConfirmActionLoading(false);
            setConfirmAction(null);
            setConfirmScope(null);
        };

        if (confirmScope.type === "row") {
            if (confirmAction === "delete") {
                deleteRow(confirmScope.id, onDone);
            } else {
                toggleStatus(confirmScope.id, onDone);
            }
        } else {
            if (confirmAction === "delete") {
                bulkDelete(onDone);
            } else if (confirmAction === "activate") {
                bulkActivate(onDone);
            } else {
                bulkSuspend(onDone);
            }
        }
    }, [confirmScope, confirmAction, confirmActionLoading, deleteRow, toggleStatus, bulkDelete, bulkSuspend, bulkActivate]);

    // Unified with Students: View now opens the same StaffProfilePage design
    // students use (/user-management/student-profile), via the parallel
    // /user-management/staff-profile route — both render the identical
    // StaffProfilePage component (it doesn't branch on the route path or
    // role), reading state.userManagementV2.userRowDetails for id/name/etc.
    // and fetching the rest itself. Previously this pushed to
    // /user-management/profile (userBasicProfileV3) instead — that route is
    // still used from elsewhere in the app (attendance, marketplace CRM, fee
    // management, etc.), just no longer from this table's View action.
    // Manage data: open the transfer-data page for this teacher (same as the old user table).
    const handleManageData = useCallback((row) => {
        updateUserToTranferData?.(row);
        history.push('/user-management/transfer-data');
    }, [updateUserToTranferData, history]);

    const handleViewProfile = useCallback((row) => {
        onViewProfile?.(row);
        setUserDetails?.(row);
        history.push(`${basePath}/staff-profile`);
    }, [onViewProfile, setUserDetails, history, basePath]);

    const startIndex = totalRecords ? (page - 1) * pageLimit + 1 : 0;
    const endIndex = totalRecords ? Math.min(page * pageLimit, totalRecords) : 0;

    const paginationLabel = totalRecords
        ? `${startIndex}-${endIndex} of ${totalRecords.toLocaleString()}`
        : `0-0 of 0`;

    const canGoPrev = page > 1 && !isLoading;
    const canGoNext = page < totalPages && !isLoading;

    const isInactiveFilter = statusFilter === "Inactive" || (selectedIds.size > 0 && filteredRows.filter((r) => selectedIds.has(r.id)).length > 0 && filteredRows.filter((r) => selectedIds.has(r.id)).every((r) => r.status === "Inactive"));

    // Pager (‹ 1-20 of N ▾ ›) — shown in the normal toolbar and, while rows
    // are selected, after the bulk-action buttons.
    const pagerNode = (
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: fluid(6),
                                        height: fluid(40),
                                        minWidth: fluid(108),
                                        boxSizing: "border-box",
                                        padding: `0 ${fluid(8)}`,
                                        borderRadius: fluid(1000),
                                        backgroundColor: "#F7F7F7",
                                        fontSize: fluid(18),
                                        lineHeight: fluid(24),
                                        fontWeight: 400,
                                        color: "#666666",
                                        whiteSpace: "nowrap",
                                    }}>
                                        <button
                                            type="button"
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={!canGoPrev}
                                            title="Previous page"
                                            style={{
                                                width: fluid(24),
                                                height: fluid(24),
                                                border: "none",
                                                borderRadius: "50%",
                                                backgroundColor: "transparent",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: canGoPrev ? "pointer" : "not-allowed",
                                                opacity: canGoPrev ? 1 : 0.4,
                                                padding: 0,
                                            }}
                                        >
                                            <img src={ArrowLeft} alt="Previous page" style={{ width: fluid(24), height: fluid(24) }} />
                                        </button>
                                        <PageSizeRange label={paginationLabel} pageSize={pageLimit} onChange={(n) => { setPageLimit(n); setPage(1); }} />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setPage((p) => Math.min(totalPages, p + 1));
                                            }}
                                            disabled={!canGoNext}
                                            title="Next page"
                                            style={{
                                                width: fluid(24),
                                                height: fluid(24),
                                                border: "none",
                                                borderRadius: "50%",
                                                backgroundColor: "transparent",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: canGoNext ? "pointer" : "not-allowed",
                                                opacity: canGoNext ? 1 : 0.4,
                                                padding: 0,
                                            }}
                                        >
                                            <img src={ArrowRight} alt="Next page" style={{ width: fluid(24), height: fluid(24) }} />
                                        </button>
                                    </div>
    );

    // First load after opening / coming back (Staff tab only — the other
    // tab's rows never send this request): a loader instead of an old copy of the list.
    const showInitialLoader = isStaff && !initialLoadDone;
    const initialLoaderNode = (
        <div ref={initialLoaderRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: fluid(260), height: initialLoaderHeight, width: '100%' }}>
            <Spin size="large" />
        </div>
    );

    return (
        <div ref={tableRootRef} style={{
            backgroundColor: "#FFFFFF",
            borderRadius: fluid(24),
            width: "100%",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            fontFamily: 'Roboto, sans-serif',
            position: 'relative',
        }}>
            <PageDataLoader visible={isLoading && (initialLoadDone || !isStaff)} />
            <div
                style={{
                    position: isMobile ? "static" : "sticky",
                    top: isMobile ? undefined : 0,
                    zIndex: 20,
                    backgroundColor: "#FFFFFF",
                    borderRadius: `${fluid(24)} ${fluid(24)} 0 0`,
                    flexShrink: 0,
                }}
            >
                <div style={{ padding: isMobile ? "18px 18px 0" : `${fluid(24)} ${fluid(28)} 0`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
                    <div className="dv2-toolbar-row" style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: fluid(14),
                        paddingBottom: fluid(18),
                    }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: fluid(4), flexShrink: 0 }}>
                            <span style={{ fontSize: fluid(20), fontWeight: 700, color: COLORS.textBlack }}>
                                {label} list <span style={{ color: COLORS.textLight, fontWeight: 500 }}>({totalRecords.toLocaleString()})</span>
                            </span>
                            {/* Grid view shows the count in "Select all (N selected)". */}
                            {selectedIds.size > 0 && viewMode === "list" && (
                                <span style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(14), fontWeight: 500, color: "#636363" }}>
                                    Selected {selectedIds.size} {selectedIds.size === 1 ? "staff" : "staffs"}
                                </span>
                            )}
                        </div>

                        <div className="dv2-toolbar-controls" style={{ display: "flex", alignItems: "center", gap: fluid(10), flexWrap: "wrap" }}>
                            {selectedIds.size > 0 ? (
                                <div style={{ display: "flex", alignItems: "center", gap: fluid(12), flexWrap: "wrap" }}>
                                    <button
                                        type="button"
                                        onClick={() => onAddAccess?.(Array.from(selectedIds))}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: fluid(40),
                                            lineHeight: 1,
                                            padding: `0 ${fluid(24)}`,
                                            boxSizing: "border-box",
                                            borderRadius: fluid(12),
                                            border: "2px solid #594099",
                                            backgroundColor: "transparent",
                                            color: "#594099",
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: fluid(14),
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                            textAlign: "center",
                                        }}
                                    >
                                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, transform: "translateY(1px)" }}>
                                            Add access
                                        </span>
                                    </button>
                                    {isInactiveFilter ? (
                                        <button
                                            type="button"
                                            onClick={requestBulkActivate}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: fluid(40),
                                                lineHeight: 1,
                                                padding: `0 ${fluid(24)}`,
                                                boxSizing: "border-box",
                                                borderRadius: fluid(12),
                                                border: "2px solid #008A67",
                                                backgroundColor: "transparent",
                                                color: "#008A67",
                                                fontFamily: "Roboto, sans-serif",
                                                fontSize: fluid(14),
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                                textAlign: "center",
                                            }}
                                        >
                                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, transform: "translateY(1px)" }}>
                                                Activate
                                            </span>
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={requestBulkSuspend}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                height: fluid(40),
                                                lineHeight: 1,
                                                padding: `0 ${fluid(24)}`,
                                                boxSizing: "border-box",
                                                borderRadius: fluid(12),
                                                border: "2px solid #FFA931",
                                                backgroundColor: "transparent",
                                                color: "#FFA931",
                                                fontFamily: "Roboto, sans-serif",
                                                fontSize: fluid(14),
                                                fontWeight: 700,
                                                textTransform: "uppercase",
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                                textAlign: "center",
                                            }}
                                        >
                                            <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, transform: "translateY(1px)" }}>
                                                Suspend
                                            </span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={requestBulkDelete}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: fluid(40),
                                            lineHeight: 1,
                                            padding: `0 ${fluid(24)}`,
                                            boxSizing: "border-box",
                                            borderRadius: fluid(12),
                                            border: "2px solid #FF414D",
                                            backgroundColor: "transparent",
                                            color: "#FF414D",
                                            fontFamily: "Roboto, sans-serif",
                                            fontSize: fluid(14),
                                            fontWeight: 700,
                                            textTransform: "uppercase",
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                            textAlign: "center",
                                        }}
                                    >
                                        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, transform: "translateY(1px)" }}>
                                            Delete
                                        </span>
                                    </button>
                                    {pagerNode}
                                </div>
                            ) : (
                                <>
                                    <div className="dv2-search-bar" style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: fluid(12),
                                        padding: `0 ${fluid(12)}`,
                                        height: fluid(40),
                                        borderRadius: fluid(1000),
                                        backgroundColor: "#FFFFFF",
                                        border: `1px solid ${COLORS.borderDark}`,
                                        width: isMobile ? "220px" : fluid(400),
                                        boxSizing: "border-box",
                                        flexShrink: 0,
                                    }}>
                                        <FiSearch size={18} color={COLORS.placeholderGray} strokeWidth={1.5} style={{ flexShrink: 0 }} />
                                        <input
                                            value={search}
                                            onChange={(e) => {
                                                setIsLoading(false);
                                                const val = e.target.value;
                                                setSearch(val);
                                                setPage(1);
                                                if (!val.trim() && allStaffCacheRef.current.length > 0) {
                                                    setStaffItems(allStaffCacheRef.current);
                                                }
                                            }}
                                            onFocus={scrollTableIntoView}
                                            placeholder="Search by name, phone No., email"
                                            className="staff-search-input"
                                            style={{
                                                border: "none",
                                                outline: "none",
                                                background: "transparent",
                                                fontSize: fluid(16),
                                                fontWeight: 400,
                                                color: COLORS.textBlack,
                                                width: "100%",
                                                minWidth: 0,
                                                textOverflow: "ellipsis",
                                            }}
                                        />
                                        {search && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSearch("");
                                                    setPage(1);
                                                    if (allStaffCacheRef.current.length > 0) {
                                                        setStaffItems(allStaffCacheRef.current);
                                                    }
                                                }}
                                                title="Clear search"
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    border: "none",
                                                    background: "transparent",
                                                    padding: 0,
                                                    cursor: "pointer",
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <FiX size={18} color={COLORS.placeholderGray} strokeWidth={1.5} />
                                            </button>
                                        )}
                                    </div>

                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        height: fluid(40),
                                        backgroundColor: "#F1F1F1",
                                        borderRadius: fluid(1000),
                                        flexShrink: 0,
                                    }}>
                                        <div ref={filterRef} style={{ position: "relative" }}>
                                            <button
                                                type="button"
                                                onClick={() => { scrollTableIntoView().then(openFilterPopover); }}
                                                style={{
                                                    width: fluid(46),
                                                    height: fluid(40),
                                                    borderRadius: `${fluid(1000)} ${fluid(8)} ${fluid(8)} ${fluid(1000)}`,
                                                    border: "none",
                                                    backgroundColor: filterOpen ? COLORS.pillBg : "#F1F1F1",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    padding: `${fluid(10)} ${fluid(10)} ${fluid(10)} ${fluid(16)}`,
                                                    boxSizing: "border-box",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <img src={FilterIcon} alt="Filter" style={{ width: fluid(20), height: fluid(20) }} />
                                            </button>

                                            {filterOpen && (
                                                <div ref={filterPanelRef} style={{
                                                    position: "absolute",
                                                    top: fluid(48),
                                                    right: 0,
                                                    minWidth: fluid(380),
                                                    backgroundColor: "#FFFFFF",
                                                    borderRadius: fluid(20),
                                                    boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                                                    padding: fluid(24),
                                                    zIndex: 30,
                                                    boxSizing: "border-box",
                                                }}>
                                                    {isStaff && (
                                                        <>
                                                            <div style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: COLORS.textBlack, marginBottom: fluid(12) }}>
                                                                Role
                                                            </div>
                                                            <PillGroup options={ROLE_PILLS} value={draftRoleFilter} onChange={setDraftRoleFilter} />
                                                        </>
                                                    )}

                                                    <div style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: COLORS.textBlack, margin: `${fluid(20)} 0 ${fluid(12)}` }}>
                                                        Status
                                                    </div>
                                                    <PillGroup options={STATUS_PILLS} value={draftStatusFilter} onChange={setDraftStatusFilter} />
                                                    <PopoverFooter disabled={isFilterDisabled} onReset={resetFilterPopover} onApply={applyFilterPopover} />
                                                </div>
                                            )}
                                        </div>

                                        <div ref={sortRef} style={{ position: "relative" }}>
                                            <button
                                                type="button"
                                                onClick={() => { scrollTableIntoView().then(openSortPopover); }}
                                                title="Sort"
                                                style={{
                                                    width: fluid(40),
                                                    height: fluid(40),
                                                    borderRadius: fluid(8),
                                                    border: "none",
                                                    backgroundColor: sortOpen ? COLORS.pillBg : "#F1F1F1",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    padding: fluid(10),
                                                    boxSizing: "border-box",
                                                    cursor: "pointer",
                                                }}
                                            >
                                                <img src={SortIcon} alt="Sort" style={{ width: fluid(20), height: fluid(20) }} />
                                            </button>

                                            {sortOpen && (
                                                <div ref={sortPanelRef} style={{
                                                    position: "absolute",
                                                    top: fluid(48),
                                                    right: 0,
                                                    minWidth: fluid(360),
                                                    backgroundColor: "#FFFFFF",
                                                    borderRadius: fluid(20),
                                                    boxShadow: "0 12px 32px rgba(0,0,0,0.16)",
                                                    padding: fluid(20),
                                                    zIndex: 30,
                                                    boxSizing: "border-box",
                                                }}>
                                                    <div style={{ fontFamily: "Roboto, sans-serif", fontSize: fluid(16), fontWeight: 500, color: COLORS.textBlack, marginBottom: fluid(12) }}>
                                                        Sort by
                                                    </div>
                                                    <PillGroup options={SORT_PILLS} value={draftSortBy} onChange={setDraftSortBy} />

                                                    <PopoverFooter disabled={isSortDisabled} onReset={resetSortPopover} onApply={applySortPopover} />
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            onClick={() => setDownloadModalOpen(true)}
                                            title="Export Excel"
                                            style={{
                                                width: fluid(46),
                                                height: fluid(40),
                                                borderRadius: `${fluid(8)} ${fluid(1000)} ${fluid(1000)} ${fluid(8)}`,
                                                border: "none",
                                                backgroundColor: "#F1F1F1",
                                                display: "flex",
                                                alignItems: "center",
                                                padding: `${fluid(10)} ${fluid(16)} ${fluid(10)} ${fluid(10)}`,
                                                boxSizing: "border-box",
                                                cursor: "pointer",
                                            }}
                                        >
                                            <img src={DownloadIcon} alt="Export" style={{ width: fluid(20), height: fluid(20) }} />
                                        </button>
                                    </div>

                                    {pagerNode}

                                    <button
                                        type="button"
                                        onClick={() => setViewMode((m) => (m === "list" ? "grid" : "list"))}
                                        title={viewMode === "list" ? "Grid view" : "List view"}
                                        style={{
                                            width: fluid(40),
                                            height: fluid(40),
                                            borderRadius: fluid(8),
                                            border: "none",
                                            outline: "none",
                                            appearance: "none",
                                            backgroundColor: "transparent",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: fluid(4),
                                            boxSizing: "border-box",
                                            boxShadow: "none",
                                            cursor: "pointer",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <img src={viewMode === "list" ? GridIcon : ListIcon} alt={viewMode === "list" ? "Grid view" : "List view"} style={{ width: fluid(32), height: fluid(32) }} />
                                    </button>

                                    <button
                                        type="button"
                                        onClick={onOpenRegistrations || (() => history.push(`${basePath}/staff-registration-requests`))}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: fluid(40),
                                            padding: `0 ${fluid(24)}`,
                                            boxSizing: "border-box",
                                            borderRadius: fluid(12),
                                            border: `2px solid ${COLORS.primary}`,
                                            backgroundColor: "transparent",
                                            color: COLORS.primary,
                                            fontSize: fluid(14),
                                            fontWeight: 700,
                                            fontFamily: "Roboto, sans-serif",
                                            lineHeight: "normal",
                                            textTransform: "uppercase",
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                        }}
                                    >
                                        Registration requests ({registrationsCount})
                                    </button>

                                    <button
                                        type="button"
                                        onClick={onAddStaff}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            height: fluid(40),
                                            padding: `0 ${fluid(32)}`,
                                            boxSizing: "border-box",
                                            borderRadius: fluid(12),
                                            border: "none",
                                            backgroundColor: COLORS.primary,
                                            color: "#FFFFFF",
                                            fontSize: fluid(16),
                                            fontWeight: 700,
                                            fontFamily: "Roboto, sans-serif",
                                            lineHeight: "normal",
                                            textTransform: "uppercase",
                                            cursor: "pointer",
                                            whiteSpace: "nowrap",
                                            flexShrink: 0,
                                        }}
                                    >
                                        Add {isStaff ? "staff" : "student"}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                </div>

                {viewMode === "list" && (
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: COLUMN_TEMPLATE,
                        alignItems: "center",
                        gap: fluid(12),
                        margin: isMobile ? "0 18px" : `0 ${fluid(25)}`,
                        padding: `${fluid(10)} ${fluid(20)}`,
                        backgroundColor: "#F4F4F4",
                        borderRadius: `${fluid(16)} ${fluid(16)} 0 0`,
                        flexShrink: 0,
                    }}>
                        <Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleAll} ariaLabel="Select all" disabled={displayedRows.length === 0} />
                        <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#1A1A1A", fontFamily: "Roboto, sans-serif" }}>Name</span>
                        <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#1A1A1A", fontFamily: "Roboto, sans-serif" }}>Contact</span>
                        <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#1A1A1A", fontFamily: "Roboto, sans-serif" }}>
                            {isStaff ? "Role" : "Class"}
                        </span>
                        <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#1A1A1A", fontFamily: "Roboto, sans-serif" }}>Access</span>
                        <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#1A1A1A", fontFamily: "Roboto, sans-serif" }}>Status</span>
                        <span style={{ fontSize: "14px", lineHeight: "24px", fontWeight: 400, color: "#1A1A1A", fontFamily: "Roboto, sans-serif" }}>Actions</span>
                    </div>
                )}
                {viewMode === "grid" && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: fluid(12),
                        height: fluid(42),
                        boxSizing: "border-box",
                        marginLeft: isMobile ? "18px" : fluid(24),
                        marginRight: isMobile ? "18px" : fluid(24),
                        flexShrink: 0,
                    }}>
                        <Checkbox checked={allChecked} indeterminate={someChecked} onChange={toggleAll} ariaLabel="Select all" disabled={displayedRows.length === 0} />
                        <span style={{ fontSize: fluid(14), fontWeight: 400, color: COLORS.textLight }}>
                            Select all{selectedIds.size > 0 ? ` (${selectedIds.size} selected)` : ""}
                        </span>
                    </div>
                )}
            </div>

            {viewMode === "list" ? (
                <div ref={rowsRegionRef} className="dv2-rows-region" style={{
                    overflowY: "visible",
                    overflowX: isMobile ? "auto" : "visible",
                    margin: isMobile ? "0 18px 16px" : `0 ${fluid(25)} ${fluid(16)}`,
                    backgroundColor: "#FFFFFF",
                    borderRadius: `0 0 ${fluid(16)} ${fluid(16)}`,
                    borderLeft: `1px solid ${COLORS.borderLight}`,
                    borderRight: `1px solid ${COLORS.borderLight}`,
                    borderBottom: `1px solid ${COLORS.borderLight}`,
                    boxSizing: "border-box",
                }}>
                    {showInitialLoader ? initialLoaderNode : filteredRows.length === 0 && !isLoading ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: `${fluid(48)} 0`,
                            width: '100%',
                            minHeight: fluid(260),
                        }}>
                            <img src={illNoData} style={{ width: fluid(220) }} alt="No data" />
                            <span style={{
                                fontSize: fluid(18),
                                fontWeight: 500,
                                color: '#636363',
                                marginTop: fluid(16),
                                fontFamily: 'Roboto, sans-serif'
                            }}>
                                No {label.toLowerCase()} match your search/filters.
                            </span>
                        </div>
                    ) : (
                        displayedRows.map((row, index) => (
                            <TableRow
                                key={row.id}
                                row={row}
                                checked={selectedIds.has(row.id)}
                                onToggleCheck={toggleCheck}
                                onToggleStatus={requestToggleStatus}
                                onDelete={requestDeleteRow}
                                onViewProfile={handleViewProfile}
                                onEditDetails={onEditDetails}
                                onManageData={handleManageData}
                                isLast={index === displayedRows.length - 1}
                                actionsDisabled={selectedIds.has(row.id)}
                            />
                        ))
                    )}


                </div>
            ) : (
                <div style={{
                    marginTop: fluid(12),
                    marginBottom: fluid(16),
                    marginLeft: isMobile ? "18px" : fluid(24),
                    marginRight: isMobile ? "18px" : fluid(24),
                    boxSizing: "border-box",
                }}>
                    {showInitialLoader ? initialLoaderNode : filteredRows.length === 0 && !isLoading ? (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: `${fluid(48)} 0`,
                            width: '100%',
                            minHeight: fluid(260),
                        }}>
                            <img src={illNoData} style={{ width: fluid(220) }} alt="No data" />
                            <span style={{
                                fontSize: fluid(18),
                                fontWeight: 500,
                                color: '#636363',
                                marginTop: fluid(16),
                                fontFamily: 'Roboto, sans-serif'
                            }}>
                                No {label.toLowerCase()} match your search/filters.
                            </span>
                        </div>
                    ) : (
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "1fr" : `repeat(auto-fill, minmax(${fluid(358)}, 1fr))`,
                            gap: fluid(24),
                            paddingBottom: fluid(24),
                            boxSizing: "border-box",
                        }}>
                            {displayedRows.map((row) => (
                                <GridCard
                                    key={row.id}
                                    row={row}
                                    checked={selectedIds.has(row.id)}
                                    // No hover expansion while Filter / Sort is open — the expanded
                                    // card would cover the popover's options.
                                    hovered={!filterOpen && !sortOpen && hoveredCardId === row.id}
                                    onHoverEnter={() => setHoveredCardId(row.id)}
                                    onHoverLeave={() => setHoveredCardId(null)}
                                    onToggleCheck={toggleCheck}
                                    onToggleStatus={requestToggleStatus}
                                    onDelete={requestDeleteRow}
                                    onViewProfile={handleViewProfile}
                                    onManageData={handleManageData}
                                    actionsDisabled={selectedIds.size > 0}
                                />
                            ))}


                        </div>
                    )}
                </div>
            )}

            <UserActionConfirmModal
                open={!!confirmScope}
                onCancel={closeConfirmModal}
                onConfirm={handleConfirmAction}
                action={confirmAction || 'suspend'}
                loading={confirmActionLoading}
                isBulk={confirmScope?.type === 'bulk' ? selectedIds.size > 1 : false}
            />

            <DownloadFileModal
                open={downloadModalOpen}
                onClose={() => setDownloadModalOpen(false)}
                fields={STAFF_EXPORT_FIELDS}
                rows={filteredRows}
                allRows={allStaffRows.length ? allStaffRows : rows}
                entityLabel="staff"
                fileName={`${label.toLowerCase()}-list`}
                sheetName={`${label} List`}
                getDate={(row) => row.joinedOn}
            />
        </div>
    );
});

StaffListTable.displayName = 'StaffListTable';

const mapStateToProps = (state) => {
    const { staffList, lastAddedStaff, staffStats } = state.userManagementV3;
    return { staffList, lastAddedStaff, staffStats };
};

const mapDispatchToProps = (dispatch) => ({
    getStaffListV3: (params, callback) => dispatch(getStaffListV3(params, callback)),
    searchStaffV3: (params, callback) => dispatch(searchStaffV3(params, callback)),
    getStaffStats: (params, callback) => dispatch(getStaffStats(params, callback)),
    updateStaffStatusV3: (userId, status, callback) => dispatch(updateStaffStatusV3(userId, status, callback)),
    bulkUpdateStaffStatus: (userIds, status, callback) => dispatch(bulkUpdateStaffStatus(userIds, status, callback)),
    setUserDetails: (row) => dispatch(setUserDetails(row)),
    updateUserToTranferData: (row) => dispatch(updateUserToTranferData(row)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StaffListTable);
